import CategoryHelmet from 'components/categories/CategoryHelmet';
import CategoryInfo from 'components/categories/CategoryInfo';
import ContactSection from 'components/contact/ContactSection';
import { useScrollCategoryList } from 'hooks/useScrollCategoryList';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { array } from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useViewContext } from 'providers/ViewContextProvider';
import styled from 'styled-components';
import { serverFetch } from 'utils/serverFetch';
import { useLocaleContext } from 'providers/LocaleProvider';

const SubCategorySection = styled.div`
  padding: 20px;

  > :not(:last-child) {
    border-bottom: solid 1px #d9d9d9;
  }

  @media screen and (max-width: 600px) {
    padding: 16px 20px 20px;

    > :not(:last-child) {
      border-bottom: none;
    }
  }
`;

const SubCategoryContent = styled.div`
  margin: 40px auto 0;
  max-width: 1160px;

  @media screen and (max-width: 600px) {
    border-radius: 4px;
    box-shadow: 0 16px 30px 0 rgba(102, 138, 184, 0.1);
    background-color: white;
    padding: 20px;
    margin: 0 auto 16px;
  }
`;

const SubCategoryTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #202325;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 0 24px 16px;

  @media screen and (max-width: 600px) {
    margin: 0 0 24px;
  }
`;

const ArticleSection = styled.ul`
  margin: auto;
  padding: 0 0 40px;
  max-width: 1160px;
  column-count: 2;

  @media screen and (max-width: 600px) {
    column-count: 1;
    padding-bottom: 0;
  }
`;

const Article = styled.a`
  -webkit-column-break-inside: avoid;
  page-break-inside: avoid;
  break-inside: avoid;
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 520px;
  font-size: 16px;
  color: #202325;
  word-break: break-word;
  margin: 0 24px 12px 16px;
  cursor: pointer;

  span {
    transition: all 0.2s ease 0s;
    line-height: 1.5;
  }

  @media screen and (max-width: 600px) {
    margin: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media screen and (min-width: 600px) {
    &:hover span {
      color: #1b92f5;
    }
  }
`;

const Oval = styled.div`
  margin-top: 3px;
  margin-right: 10.5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  background-image: url('/images/ic_article.png');
`;

function CategoryPage({ categories, subCategories }) {
  const { query, asPath } = useRouter();
  const hash = asPath.split('#')[1];
  const currentCategoryId = parseInt(query.categoryId, 10);
  const { isMobileView } = useViewContext();
  const currentCategory = useMemo(() => {
    return categories.find((cat) => cat.id === currentCategoryId);
  }, [categories, currentCategoryId]);
  const { isScrollDown, beyondThreshold } = useScrollCategoryList();
  const { language } = useLocaleContext();

  // Scroll to section matching hash in URL --------------
  useEffect(() => {
    if (hash) {
      const domElement = document.querySelector(
        `[data-help-center-category-id="${hash}"]`
      );

      if (domElement) {
        window.scroll({
          top: domElement.offsetTop - (isMobileView ? 135 : 80),
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  }, [hash, isMobileView]);
  // ------------------------------------------------------

  return (
    <>
      <CategoryHelmet
        title={currentCategory.name}
        description={currentCategory.description}
        language={language}
      />

      <CategoryInfo
        categories={categories}
        currentCategoryId={currentCategoryId}
        isScrollDown={isScrollDown}
        beyondThreshold={beyondThreshold}
      />

      <SubCategorySection>
        {subCategories.map((subCat) => (
          <SubCategoryContent key={subCat.id}>
            <SubCategoryTitle data-help-center-category-id={subCat.id}>
              {subCat.name}
            </SubCategoryTitle>

            <ArticleSection>
              {subCat.articles.map((article) => (
                <Link
                  key={article.id}
                  href={`/categories/${currentCategoryId}/article/${article.id}`}
                >
                  <Article>
                    <Oval /> <span>{article.title}</span>
                  </Article>
                </Link>
              ))}
            </ArticleSection>
          </SubCategoryContent>
        ))}
      </SubCategorySection>

      {isMobileView && <ContactSection />}
    </>
  );
}

CategoryPage.propTypes = {
  categories: array,
  subCategories: array,
};

export async function getServerSideProps(context) {
  const { params } = context;

  try {
    const [categoriesResponse, articlesResponse] = await Promise.all([
      serverFetch(context, `/categories/`, 'GET'),
      serverFetch(context, `/categories/${params.categoryId}/articles/`, 'GET'),
    ]);

    if (
      !categoriesResponse.data.categories.find(
        (category) => category.id.toString() === params.categoryId
      )
    ) {
      return { notFound: true };
    }

    return {
      props: {
        categories: categoriesResponse.data.categories,
        subCategories: articlesResponse.data.subCategories,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

export default CategoryPage;
